// src/components/SimpleComponents/SimpleCurrency.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, Select, Space } from 'antd';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import useSubscriptionLocale from 'hooks/useSubscriptionLocale';

const { Option } = Select;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEFAULT_CURRENCIES = [
  { value: 'BRL', label: 'R$', text: 'Brazilian Real' },
  { value: 'USD', label: '$', text: 'US Dollar' },
  { value: 'EUR', label: '\u20ac', text: 'Euro' },
];

const LOCALE_CONFIGS = {
  'pt-BR': { thousandSeparator: '.', decimalSeparator: ',' },
  'en-US': { thousandSeparator: ',', decimalSeparator: '.' },
  default: { thousandSeparator: ',', decimalSeparator: '.' },
};

const CURRENCY_ADDON_BASE_STYLE = {
  padding: '0 11px',
  display: 'flex',
  alignItems: 'center',
  border: '1px solid var(--ant-color-border)',
  borderLeft: 'none',
  borderRadius: '0 6px 6px 0',
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleCurrency = ({
  name,
  style = {},
  subdocumentField,

  // Locale and formatting (undefined = use subscription locale automatically)
  locale,

  showThousandSeparator = true,
  showDecimalSeparator = true,
  customThousandSeparator,
  customDecimalSeparator,

  // Number config
  decimalPlaces = 2,
  min = 0,
  max = 999999999.99,
  rounding = 'natural', // 'up' | 'down' | 'natural'

  // Currency suffix (undefined = auto-derive single currency from subscription locale)
  currencies,
  currencyDefaultValue,
  onCurrencyChange,
  showCurrencySelect = true,
  currencyEditable = false, // When false (default), currency suffix is disabled/read-only
  'x-currency-style': xCurrencyStyle = { minWidth: 40 },

  // Prefix (component or select options)
  prefix,
  prefixDefaultValue,
  onPrefixChange,
  'x-prefix-style': xPrefixStyle = {},

  // Other props
  disabled,
  onBlur,
  onChange,
  onClick,
  readOnly,
  placeholder,

  ...props
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { locked, disabledFields, form } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const { customValidationStatus, customValidationMessage } = useCustomValidationStatus({
    ...props,
    ...elementProps,
  });
  const subscriptionLocale = useSubscriptionLocale();

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // Auto-derive locale currency: if caller passes a single hardcoded currency that doesn't
  // match the subscription locale default, replace it transparently. Multi-currency arrays
  // (explicit choice UI) are always kept as-is.
  const effectiveCurrencies = (() => {
    const localeDef = subscriptionLocale.defaultCurrency;
    if (!currencies || currencies.length === 0) {
      const match = DEFAULT_CURRENCIES.find(c => c.value === localeDef);
      return match ? [match] : [DEFAULT_CURRENCIES[0]];
    }
    if (currencies.length === 1 && localeDef && currencies[0].value !== localeDef) {
      const match = DEFAULT_CURRENCIES.find(c => c.value === localeDef);
      if (match) return [match];
    }
    return currencies;
  })();

  const localeConfig = locale
    ? LOCALE_CONFIGS[locale] || LOCALE_CONFIGS.default
    : { thousandSeparator: subscriptionLocale.thousandSeparator, decimalSeparator: subscriptionLocale.decimalSeparator };

  const thousandSep =
    customThousandSeparator !== undefined
      ? customThousandSeparator
      : showThousandSeparator
        ? localeConfig.thousandSeparator
        : '';

  const decimalSep =
    customDecimalSeparator !== undefined
      ? customDecimalSeparator
      : showDecimalSeparator
        ? localeConfig.decimalSeparator
        : '';

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [displayValue, setDisplayValue] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(
    currencyDefaultValue || effectiveCurrencies?.[0]?.value
  );
  const [selectedPrefix, setSelectedPrefix] = useState(prefixDefaultValue);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const isFocusedRef = useRef(false);

  // Watch form value changes using Form.useWatch
  const watchedValue = Form.useWatch(fullFieldName, form);

  // Watch currency suffix field (if name is ['parameters', '__exchangeRate'], watch ['parameters', '__exchangeRateSuffix'])
  const currencySuffixFieldName = Array.isArray(fullFieldName)
    ? [...fullFieldName.slice(0, -1), '__exchangeRateSuffix']
    : undefined;

  const watchedCurrency = currencySuffixFieldName ? Form.useWatch(currencySuffixFieldName, form) : undefined;

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  // Sync selectedPrefix with prefixDefaultValue when it changes (e.g., after load)
  useEffect(() => {
    if (prefixDefaultValue && prefixDefaultValue !== selectedPrefix) {
      setSelectedPrefix(prefixDefaultValue);
    }
  }, [prefixDefaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedCurrency with currencyDefaultValue when it changes (e.g., after load)
  useEffect(() => {
    if (currencyDefaultValue && currencyDefaultValue !== selectedCurrency) {
      setSelectedCurrency(currencyDefaultValue);
    }
  }, [currencyDefaultValue]);

  // Sync selectedCurrency with form value when it changes (e.g., after sync)
  useEffect(() => {
    if (watchedCurrency && watchedCurrency !== selectedCurrency) {
      setSelectedCurrency(watchedCurrency);
    }
  }, [watchedCurrency, selectedCurrency]);

  // Sync display value with form value when it changes externally (not while user is editing)
  useEffect(() => {
    // Don't update display while user is focused/editing
    if (isFocusedRef.current) {
      return;
    }

    // Handle both number and formatted string
    let numericValue = watchedValue;

    // If it's a formatted string, parse it back to number
    if (typeof watchedValue === 'string' && watchedValue.trim() !== '') {
      // Remove thousand separators and replace decimal separator
      const cleaned = watchedValue
        .replace(new RegExp(`\\${thousandSep}`, 'g'), '')
        .replace(decimalSep, '.');
      numericValue = parseFloat(cleaned);
    }

    if (numericValue !== undefined && numericValue !== null && !isNaN(numericValue)) {
      const formattedValue = formatCurrency(numericValue, {
        thousandSep,
        decimalSep,
        decimalPlaces,
      });
      setDisplayValue(formattedValue);
    } else if (watchedValue === undefined || watchedValue === null || watchedValue === '') {
      setDisplayValue('');
    }
  }, [watchedValue, thousandSep, decimalSep, decimalPlaces]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleInputChange = useCallback(
    ev => {
      const rawValue = ev?.target?.value ?? '';

      // Allow typing: only digits and decimal separator
      const regex = new RegExp(`^[0-9${escapeRegex(decimalSep)}]*$`);
      if (!regex.test(rawValue) && rawValue !== '') return;

      // Prevent multiple decimal separators
      if (decimalSep) {
        const sepCount = (rawValue.match(new RegExp(escapeRegex(decimalSep), 'g')) || []).length;
        if (sepCount > 1) return;
      }

      // Update display value while typing (raw, unformatted)
      setDisplayValue(rawValue);

      // Parse and set form value
      const numericValue = parseLocaleNumber(rawValue, decimalSep);

      if (!isNaN(numericValue) && rawValue !== '') {
        form?.setFieldValue(fullFieldName, numericValue);
        onChange?.(numericValue);
      } else if (rawValue === '') {
        form?.setFieldValue(fullFieldName, null);
        onChange?.(null);
      }
    },
    [decimalSep, form, fullFieldName, onChange]
  );

  const handleBlur = useCallback(
    ev => {
      isFocusedRef.current = false;
      const rawValue = ev?.target?.value ?? '';

      if (rawValue === '') {
        setDisplayValue('');
        onBlur?.(ev);
        return;
      }

      // Parse current value
      const cleanValue = rawValue.replace(new RegExp(`[${escapeRegex(thousandSep)}]`, 'g'), '');
      let numericValue = parseLocaleNumber(cleanValue, decimalSep);

      if (isNaN(numericValue)) {
        setDisplayValue('');
        form?.setFieldValue(fullFieldName, null);
        onBlur?.(ev);
        return;
      }

      // Apply min/max
      if (min !== undefined && numericValue < min) numericValue = min;
      if (max !== undefined && numericValue > max) numericValue = max;

      // Apply rounding
      numericValue = applyRounding(numericValue, decimalPlaces, rounding);

      // Format final value
      const formattedValue = formatCurrency(numericValue, {
        thousandSep,
        decimalSep,
        decimalPlaces,
      });

      setDisplayValue(formattedValue);
      form?.setFieldValue(fullFieldName, numericValue);

      onBlur?.(ev);
    },
    [thousandSep, decimalSep, decimalPlaces, rounding, min, max, form, fullFieldName, onBlur]
  );

  const handleFocus = useCallback(
    ev => {
      isFocusedRef.current = true;

      // Remove thousand separators for easier editing
      const currentValue = ev?.target?.value ?? '';
      if (thousandSep && currentValue.includes(thousandSep)) {
        const cleanValue = currentValue.replace(new RegExp(escapeRegex(thousandSep), 'g'), '');
        setDisplayValue(cleanValue);
      }

      // Select all text on focus for easy editing
      setTimeout(() => {
        ev?.target?.select?.();
      }, 0);
    },
    [thousandSep]
  );

  const handleCurrencyChange = useCallback(
    value => {
      setSelectedCurrency(value);
      onCurrencyChange?.(value);
    },
    [onCurrencyChange]
  );

  const handlePrefixChange = useCallback(
    value => {
      setSelectedPrefix(value);
      onPrefixChange?.(value);
    },
    [onPrefixChange]
  );

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const hasPrefix = !!prefix;
  const hasSuffix = showCurrencySelect && effectiveCurrencies?.length > 0;
  const needsCompact = hasPrefix || hasSuffix;
  const inputStyle = { width: needsCompact ? undefined : '100%', flex: needsCompact ? 1 : undefined, height: 40, textAlign: 'right', ...style };

  // Build prefix element
  const prefixElement = useMemo(() => {
    if (!prefix) return null;

    // If prefix is array of options, render Select
    if (Array.isArray(prefix) && typeof prefix[0] === 'object') {
      return (
        <Select
          disabled={isDisabled}
          value={selectedPrefix}
          defaultValue={prefixDefaultValue}
          onChange={handlePrefixChange}
          style={{ minWidth: 80, ...xPrefixStyle }}
          getPopupContainer={trigger => trigger.parentNode}
        >
          {prefix.map(opt => (
            <Option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label ?? opt.text ?? opt.value}
            </Option>
          ))}
        </Select>
      );
    }

    // If prefix is a React element or string, return as-is
    return prefix;
  }, [prefix, isDisabled, selectedPrefix, prefixDefaultValue, handlePrefixChange, xPrefixStyle]);

  // Build currency suffix element
  const currencySuffixElement = useMemo(() => {
    if (!showCurrencySelect || !effectiveCurrencies?.length) return null;

    if (effectiveCurrencies.length === 1) {
      // Single currency - show as text (using Ant Design Input addon style)
      return (
        <span
          className="ant-input-group-addon"
          style={{ ...CURRENCY_ADDON_BASE_STYLE, ...xCurrencyStyle }}
        >
          {effectiveCurrencies[0].label}
        </span>
      );
    }

    // Multiple currencies - show Select (wider to accommodate dropdown arrow)
    // Disabled when field is disabled OR when currencyEditable is false
    return (
      <Select
        disabled={isDisabled || !currencyEditable}
        value={selectedCurrency}
        defaultValue={currencyDefaultValue || effectiveCurrencies[0]?.value}
        onChange={handleCurrencyChange}
        style={{ width: 70, minWidth: 70, ...xCurrencyStyle }}
        getPopupContainer={() => document.body}
      >
        {effectiveCurrencies.map(opt => (
          <Option key={opt.value} value={opt.value} disabled={opt.disabled} title={opt.text}>
            {opt.label}
          </Option>
        ))}
      </Select>
    );
  }, [
    showCurrencySelect,
    effectiveCurrencies,
    isDisabled,
    currencyEditable,
    selectedCurrency,
    currencyDefaultValue,
    handleCurrencyChange,
    xCurrencyStyle,
  ]);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  const inputElement = (
    <Input
      ref={elementRef}
      id={flattenFieldName}
      style={inputStyle}
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onClick={onClick}
      readOnly={readOnly}
      disabled={isDisabled}
      placeholder={placeholder ?? '0' + decimalSep + '0'.repeat(decimalPlaces)}
    />
  );

  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      {needsCompact ? (
        <Space.Compact style={{ width: '100%' }}>
          {prefixElement}
          {inputElement}
          {currencySuffixElement}
        </Space.Compact>
      ) : (
        inputElement
      )}
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCurrency;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function escapeRegex(str) {
  if (!str) return '';
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseLocaleNumber(value, decimalSep) {
  if (!value || value === '') return NaN;

  // Replace locale decimal separator with standard dot
  const normalized = value.replace(decimalSep, '.');
  return parseFloat(normalized);
}

function applyRounding(value, decimalPlaces, rounding) {
  if (isNaN(value)) return value;

  const multiplier = Math.pow(10, decimalPlaces);

  switch (rounding) {
    case 'up':
      return Math.ceil(value * multiplier) / multiplier;
    case 'down':
      return Math.floor(value * multiplier) / multiplier;
    case 'natural':
    default:
      return Math.round(value * multiplier) / multiplier;
  }
}

function formatCurrency(value, { thousandSep, decimalSep, decimalPlaces }) {
  if (value === null || value === undefined) return '';

  // Ensure value is a number
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) return '';

  // Format with fixed decimal places
  const parts = numValue.toFixed(decimalPlaces).split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';

  // Add thousand separators
  let formattedInteger = integerPart;
  if (thousandSep) {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
  }

  // Combine with decimal separator
  if (decimalPlaces > 0 && decimalSep) {
    return `${formattedInteger}${decimalSep}${decimalPart}`;
  }

  return formattedInteger;
}
